"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { ProductSection } from "@/components/product-section"
import { ProductCarousel } from "@/components/product-carousel"
import { PromotionsBlock } from "@/components/promotions-block"
import { AnnouncementCarousel } from "@/components/announcement-carousel"
import type { Product } from "@/lib/types"
import { apiStore } from "@/lib/api-store"
import { normalizeIds } from "@/lib/normalize"
import { ArrowRight, Search, SlidersHorizontal, Sparkles, TrendingUp, Eye, Heart, Package } from "lucide-react"
import Link from "next/link"

// ─── Skeleton ────────────────────────────────────────────────────────────────

function SkeletonGrid() {
  return (
    <div className="container py-10">
      {[0, 1].map((section) => (
        <div key={section} className="mb-12">
          <div className="mb-6 flex items-center gap-3">
            <div className="h-6 w-24 animate-pulse rounded-full bg-background-secondary" />
            <div className="h-7 w-48 animate-pulse rounded-lg bg-background-secondary" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-border bg-background">
                <div className="h-52 w-full animate-pulse bg-background-secondary" />
                <div className="space-y-2 p-4">
                  <div className="h-4 w-3/4 animate-pulse rounded bg-background-secondary" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-background-secondary" />
                  <div className="h-5 w-1/3 animate-pulse rounded bg-background-secondary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Section wrapper cohérent avec le design system ──────────────────────────

function SectionHeader({
  badge,
  title,
  href,
  sectionType,
}: {
  badge: string
  title: string
  href?: string
  sectionType?: string
}) {
  const link = href || (sectionType ? `/recherche?sort=${sectionType}` : undefined)
  return (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <span className="inline-block rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
          {badge}
        </span>
        <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-foreground">{title}</h2>
      </div>
      {link && (
        <Link
          href={link}
          className="group hidden shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition hover:underline sm:flex"
        >
          Voir tout <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </Link>
      )}
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Boutique() {
  const router = useRouter()
  const [products, setProducts] = useState<Product[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [categories, setCategories] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const allProducts = await apiStore.getProducts()
        const normalized = normalizeIds(allProducts)
        setProducts(normalized)
        const cats = Array.from(new Set(normalized.map((p) => p.category).filter(Boolean)))
        setCategories(cats)
      } catch (error) {
        console.error("Error fetching products:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/recherche?q=${encodeURIComponent(searchQuery.trim())}`)
    }
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    if (category !== "all") {
      router.push(`/recherche?category=${encodeURIComponent(category)}`)
    }
  }

  const bestSellers = [...products].sort((a, b) => (b.sales ?? 0) - (a.sales ?? 0)).slice(0, 4)
  const mostViewed = [...products].sort((a, b) => (b.views ?? 0) - (a.views ?? 0)).slice(0, 4)
  const mostLiked = [...products].sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0)).slice(0, 4)
  const newArrivals = [...products]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 4)
  const suggestions = products.slice(0, 4)

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />

      <main className="flex-1">

        {/* ── HERO ──────────────────────────────────────────────────── */}
        <section className="relative flex min-h-[420px] items-center overflow-hidden lg:min-h-[480px]">

          {/* fond sombre */}
          <div className="absolute inset-0" style={{ background: "#080f1c" }} />

          {/* image à droite */}
          <div className="absolute right-0 top-0 h-full w-full lg:w-[55%]">
            <img
              src="/covernew.jpg"
              alt="Instruments de musique"
              className="h-full w-full object-cover object-center"
              loading="eager"
            />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to right, #080f1c 0%, rgba(8,15,28,0.9) 15%, rgba(8,15,28,0.5) 45%, rgba(8,15,28,0.15) 75%, transparent 100%)",
              }}
            />
            <div className="absolute inset-0 bg-black/25" />
          </div>

          {/* grille */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          <div className="container relative py-14 lg:py-20">
            <div className="mx-auto max-w-2xl text-center">

              <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight text-white md:text-5xl">
                Notre{" "}
                <span
                  style={{
                    background: "linear-gradient(90deg, #60a5fa 0%, #a78bfa 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  boutique
                </span>
              </h1>
              <p className="mt-3 text-base text-white/55">
                Instruments, sonorisation, éclairage et accessoires — tout pour votre son.
              </p>

              {/* Search */}
              <form onSubmit={handleSearch} className="mt-8 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                  <input
                    type="text"
                    placeholder="Rechercher un produit, une marque…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-12 w-full rounded-full border border-white/20 bg-white/10 pl-11 pr-4 text-sm text-white placeholder-white/40 backdrop-blur-sm transition focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20"
                  />
                </div>
                <button
                  type="submit"
                  className="flex h-12 items-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-white transition hover:bg-primary/90 hover:shadow-lg"
                >
                  <Search size={15} />
                  <span className="hidden sm:inline">Rechercher</span>
                </button>
                <Link
                  href="/recherche"
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-white/8 text-white/60 backdrop-blur-sm transition hover:bg-white/15 hover:text-white"
                  title="Filtres avancés"
                >
                  <SlidersHorizontal size={16} />
                </Link>
              </form>

              {/* Category pills */}
              {categories.length > 0 && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <button
                    onClick={() => handleCategoryChange("all")}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${selectedCategory === "all"
                      ? "bg-white text-primary shadow"
                      : "border border-white/20 bg-white/8 text-white/70 hover:bg-white/15 hover:text-white"
                      }`}
                  >
                    Tous
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => handleCategoryChange(cat)}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all ${selectedCategory === cat
                        ? "bg-white text-primary shadow"
                        : "border border-white/20 bg-white/8 text-white/70 hover:bg-white/15 hover:text-white"
                        }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── PROMOTIONS ────────────────────────────────────────────── */}
        <PromotionsBlock />

        {/* ── ANNONCES ──────────────────────────────────────────────── */}
        <AnnouncementCarousel title="Annonces" autoplayInterval={3500} limit={12} />

        {/* ── CARROUSEL DÉCOUVERTE ──────────────────────────────────── */}
        <ProductCarousel title="Découvrez nos produits" autoplayInterval={3000} />

        {/* ── SECTIONS PRODUITS ─────────────────────────────────────── */}
        {loading ? (
          <SkeletonGrid />
        ) : (
          <div className="bg-background">

            {/* Nouveautés */}
            <div className="border-t border-border">
              <div className="container py-12">
                <SectionHeader badge="Arrivages" title="Nouveaux produits" sectionType="nouveautes" />
                <ProductSection title="" products={newArrivals} sectionType="nouveautes" />
              </div>
            </div>

            {/* Best sellers */}
            <div className="border-t border-border bg-background-secondary">
              <div className="container py-12">
                <SectionHeader badge="Best-sellers" title="Les plus vendus" sectionType="meilleures-ventes" />
                <ProductSection title="" products={bestSellers} sectionType="meilleures-ventes" />
              </div>
            </div>

            {/* Plus vus */}
            <div className="border-t border-border">
              <div className="container py-12">
                <SectionHeader badge="Tendances" title="Les plus consultés" sectionType="plus-vus" />
                <ProductSection title="" products={mostViewed} sectionType="plus-vus" />
              </div>
            </div>

            {/* Plus aimés */}
            <div className="border-t border-border bg-background-secondary">
              <div className="container py-12">
                <SectionHeader badge="Coups de cœur" title="Les plus appréciés" sectionType="plus-aimes" />
                <ProductSection title="" products={mostLiked} sectionType="plus-aimes" />
              </div>
            </div>

            {/* Suggestions */}
            <div className="border-t border-border">
              <div className="container py-12">
                <SectionHeader badge="Sélection" title="Nos suggestions" sectionType="suggestions" />
                <ProductSection title="" products={suggestions} sectionType="suggestions" />
              </div>
            </div>

          </div>
        )}

        {/* ── CTA BAS DE PAGE ───────────────────────────────────────── */}
        <section
          className="relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0a1628 0%, #12284a 55%, #0d1f3c 100%)" }}
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          <div className="pointer-events-none absolute -left-32 top-0 h-80 w-80 rounded-full opacity-20 blur-3xl" style={{ background: "#2f5d97" }} />
          <div className="container relative py-14 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">Vous ne trouvez pas ?</p>
            <h2 className="mt-3 text-3xl font-extrabold text-white">
              Contactez-nous directement
            </h2>
            <p className="mx-auto mt-3 max-w-md text-base text-white/55">
              Notre équipe peut vous aider à trouver l'instrument ou l'équipement qui correspond exactement à votre besoin.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <Link
                href="/contact"
                className="group inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-white shadow-lg transition-all hover:scale-105"
                style={{ background: "linear-gradient(135deg, #2f5d97 0%, #3b74b5 100%)" }}
              >
                Nous contacter
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                href="/recherche"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/8 px-7 py-3.5 text-sm font-bold text-white backdrop-blur-sm transition-all hover:bg-white/15"
              >
                Recherche avancée
              </Link>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  )
}
